import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Layers, Users, AlertCircle, Eye } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { ProductsService, type Product } from '@/services/modules/ProductsService'
import { toast } from '@/components/ui/Toast'
import type { PaginationMeta } from '@/types/api'
import { type PageSize } from '@/hooks/useCrudList'
import { useAuthority } from '@/hooks/useAuthority'

/**
 * Listagem de Produtos. Form em página dedicada (`/novo`, `/:id/editar`) —
 * regra global do Comercial.
 */
export function ProductsListPage() {
  const nav = useNavigate()
  const [reload, setReload] = useState(0)
  // Rotas em /commercial/products — as chaves são do módulo comercial.
  const { can } = useAuthority()
  const canCreate = can('commercial.products.create')
  const canEdit = can('commercial.products.edit')
  const canDelete = can('commercial.products.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar produto' })
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [rows, setRows] = useState<Product[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [openDelete, setOpenDelete] = useState<Product | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    ProductsService.list({ page, size, ...sf.filters })
      .then(res => {
        if (!active) return
        setRows(res.data)
        setMeta(res.meta)
      })
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [page, size, sf.filters, reload])

  const exp = useDataTableExport({
    filename: 'produtos',
    service: ProductsService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',   accessor: (p: Product) => p.name },
      { header: 'Status', accessor: (p: Product) => p.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try {
      await ProductsService.destroy(openDelete.id)
      toast.success({ title: 'Produto removido', description: openDelete.name })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao remover.'
      toast.error({ title: 'Não foi possível remover', description: msg })
    }
  }

  const columns: DataTableColumn<Product>[] = [
    {
      key: 'name',
      header: 'Produto',
      sortable: true,
      sortKey: 'name',
      alwaysVisible: true,
      render: p => (
        <div>
          <div className="font-medium text-gray-800">{p.name}</div>
          {p.description && <div className="text-[11px] text-gray-500 break-words">{p.description}</div>}
        </div>
      ),
    },
    {
      key: 'composition',
      header: 'Composição',
      align: 'center',
      className: 'w-36',
      render: p => {
        const list = p.services ?? []
        if (list.length === 0) return <span className="text-xs text-gray-400">—</span>
        return (
          <InfoPopover
            ariaLabel={`${list.length} serviços`}
            title="Serviços × Prestadores"
            trigger={
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200">
                <Layers size={12} />
                {list.length}
              </span>
            }
          >
            <ul className="space-y-1 text-sm">
              {list.map(s => (
                <li key={s.id} className="text-gray-700">
                  <span className="font-medium">{s.serviceName}</span>
                  <span className="text-gray-400"> — </span>
                  <span>{s.providerName}</span>
                </li>
              ))}
            </ul>
          </InfoPopover>
        )
      },
    },
    {
      key: 'modalities',
      header: 'Modalidades',
      align: 'center',
      className: 'w-36',
      render: p => {
        const list = p.modalities ?? []
        if (list.length === 0) return <span className="text-xs text-gray-400">—</span>
        return (
          <InfoPopover
            ariaLabel={`${list.length} modalidades`}
            title="Modalidades disponíveis"
            trigger={
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200">
                <Users size={12} />
                {list.length}
              </span>
            }
          >
            <ul className="space-y-1 text-sm">
              {list.map(m => (
                <li key={m.id} className="text-gray-700">{m.planTypeRelationship?.name ?? '—'}</li>
              ))}
            </ul>
          </InfoPopover>
        )
      },
    },
    {
      key: 'rules',
      header: 'Elegibilidade',
      align: 'center',
      className: 'w-36',
      render: p => {
        const list = p.modalities ?? []
        const totalRules = list.reduce((acc, m) => acc + (m.degrees?.length ?? 0), 0)
        if (totalRules === 0) return <span className="text-xs text-gray-400">—</span>
        return (
          <InfoPopover
            ariaLabel={`${totalRules} regras`}
            title="Regras de elegibilidade"
            minWidth={320}
            maxWidth={480}
            trigger={
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200">
                <AlertCircle size={12} />
                {totalRules}
              </span>
            }
          >
            <div className="space-y-3">
              {list.map(m => (
                <div key={m.id}>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {m.planTypeRelationship?.name ?? '—'}
                  </div>
                  {m.degrees.length === 0 ? (
                    <p className="text-xs text-gray-400">Sem regras.</p>
                  ) : (
                    <ul className="space-y-0.5 text-sm">
                      {m.degrees.map(d => {
                        const ages = d.minAge != null || d.maxAge != null
                          ? ` (${d.minAge ?? '—'}–${d.maxAge ?? '—'} anos)`
                          : ''
                        const extra = d.additionalPolicies > 0 ? ` · +${d.additionalPolicies} apólice${d.additionalPolicies === 1 ? '' : 's'}` : ''
                        return (
                          <li key={d.id} className="text-gray-700">
                            <span className="font-medium">{d.degree?.degreeRelationship ?? '—'}</span>
                            <span className="text-gray-500">{ages}{extra}</span>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </InfoPopover>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      className: 'w-24',
      render: p => <StatusDot value={p.status} />,
    },
  ]

  return (
    <>
      <PageHeader
        title="Produtos"
        subtitle="Bundles de serviços × prestadores + regras de elegibilidade por modalidade"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Produtos' }]}
        actions={canCreate ? (
          <Button
            onClick={() => nav('/settings/commercial/products/novo')}
            iconLeft={<Plus size={14} />}
          >
            Novo produto
          </Button>
        ) : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.commercial.products"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={p => p.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(p) => [
          // Sem `edit`, o formulário abre só para consulta (o Salvar fica desabilitado lá).
          canEdit
            ? { key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => nav(`/settings/commercial/products/${p.id}/editar`) }
            : { key: 'view', label: 'Visualizar', icon: <Eye size={14} />, onClick: () => nav(`/settings/commercial/products/${p.id}/editar`) },
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: true, onClick: () => setOpenDelete(p) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover produto?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? Esta ação não pode ser desfeita. Planos vinculados a este produto perderão a referência.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}
